import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Container } from '@/components/site/Section';
import { CONTACT } from '@/components/site/SiteFooter';
import { apiGet, formatKr, formatNumber } from '@/lib/api';
import { formatDateLong } from '@/domain/time';

const METHOD = { hent: 'Hent selv', tippbil: 'Tippbil', kranbil: 'Big bag med kranbil', baat: 'Båt', ukjent: 'Ikke avklart' };

function Row({ label, children, testId }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-6">
      <dt className="text-[13.5px] text-[var(--hr-muted)]">{label}</dt>
      <dd className="text-[14.5px] font-medium text-[var(--hr-heading)] sm:text-right" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

export function ConfirmationShell({ kind, id, loading, error, children, title, lead }) {
  return (
    <div className="bg-[var(--hr-bg)]" data-testid={`${kind}-confirmation-page`}>
      <Container className="py-14 sm:py-20">
        <div className="mx-auto max-w-[680px]">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--hr-green-bg)] text-[var(--hr-green)]">
              <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
            </span>
            <h1 className="mt-6 font-display text-[30px] font-semibold tracking-[-0.02em] text-[var(--hr-heading)] sm:text-[38px]" data-testid="confirmation-title">
              {title}
            </h1>
            <p className="mt-3 max-w-[52ch] text-[15.5px] leading-7 text-[var(--hr-muted)]">{lead}</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--hr-hairline)] bg-white px-4 py-2 text-[14px] shadow-[var(--hr-shadow-xs)]">
              <span className="text-[var(--hr-muted)]">Referanse</span>
              <span className="hr-tabular font-semibold text-[var(--hr-heading)]" data-testid="confirmation-id">
                {id}
              </span>
            </div>
          </div>

          <div className="hr-surface mt-10 overflow-hidden">
            {loading ? (
              <div className="space-y-3 p-6" aria-busy="true">
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-5 w-1/3" />
              </div>
            ) : error ? (
              <div className="p-6 text-[14.5px] text-[var(--hr-muted)]" data-testid="confirmation-error">
                Vi fant ikke detaljene akkurat nå, men referansen over er registrert. {error}
              </div>
            ) : (
              children
            )}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="hr-surface flex items-center gap-3 p-5">
              <Phone className="h-5 w-5 text-[var(--hr-accent-text)]" aria-hidden="true" />
              <div>
                <div className="text-[12.5px] text-[var(--hr-muted)]">Spørsmål? Ring anlegget</div>
                <a href={CONTACT.plant.phoneHref} className="hr-tabular text-[15px] font-semibold text-[var(--hr-heading)]">
                  {CONTACT.plant.phone}
                </a>
              </div>
            </div>
            <div className="hr-surface flex items-center gap-3 p-5">
              <Mail className="h-5 w-5 text-[var(--hr-accent-text)]" aria-hidden="true" />
              <div>
                <div className="text-[12.5px] text-[var(--hr-muted)]">Eller send e-post</div>
                <a href={`mailto:${CONTACT.plant.email}`} className="text-[15px] font-semibold text-[var(--hr-heading)]">
                  {CONTACT.plant.email}
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <Button asChild variant="outline" className="h-11 rounded-[10px] bg-white px-5">
              <Link to="/" data-testid="confirmation-home-link">
                Til forsiden <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function OrderConfirmationPage() {
  const { id } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order ?? null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = `Bestilling ${id} · Hardanger Rock`;
    if (order) return;
    let cancelled = false;
    apiGet(`/orders/${id}`)
      .then((o) => !cancelled && setOrder(o))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [id, order]);

  return (
    <ConfirmationShell kind="order" id={id} loading={!order && !error} error={error} title="Takk for bestillingen" lead="Vi har mottatt bestillingen og tar kontakt for å bekrefte leveringsdato, frakt og endelig pris.">
      {order && (
        <>
          <div className="border-b border-[var(--hr-hairline)] px-6 py-4">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[var(--hr-muted-2)]">Produkter</h2>
            <ul className="mt-3 divide-y divide-[var(--hr-hairline)]" data-testid="confirmation-items">
              {order.items.map((i) => (
                <li key={i.slug} className="flex items-center justify-between gap-4 py-2.5 text-[14.5px]">
                  <span className="font-medium text-[var(--hr-heading)]">
                    {i.name} <span className="hr-tabular text-[var(--hr-muted)]">· {formatNumber(i.quantityTonnes, 1)} t</span>
                  </span>
                  <span className="hr-tabular font-medium text-[var(--hr-heading)]">{i.lineTotalExVat !== null ? formatKr(i.lineTotalExVat) : 'Prosjektpris'}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-baseline justify-between border-t border-[var(--hr-hairline)] pt-3">
              <span className="text-[14px] text-[var(--hr-muted)]">Estimert eks. mva</span>
              <span className="hr-tabular font-display text-[22px] font-semibold text-[var(--hr-heading)]" data-testid="confirmation-total">{formatKr(order.estimatedTotalExVat)}</span>
            </div>
          </div>
          <dl className="space-y-3 px-6 py-5">
            <Row label="Levering">{METHOD[order.delivery.method] ?? order.delivery.method}</Row>
            {order.delivery.address && <Row label="Adresse">{[order.delivery.address, [order.delivery.postalCode, order.delivery.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</Row>}
            {order.delivery.requestedDate && <Row label="Ønsket dato">{formatDateLong(order.delivery.requestedDate)}</Row>}
            <Row label="Kontakt">
              {order.customer.name}
              {order.customer.company ? `, ${order.customer.company}` : ''}
            </Row>
            <Row label="E-post">{order.customer.email}</Row>
          </dl>
        </>
      )}
    </ConfirmationShell>
  );
}

export function RequestConfirmationPage() {
  const { id } = useParams();
  const location = useLocation();
  const [req, setReq] = useState(location.state?.request ?? null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = `Forespørsel ${id} · Hardanger Rock`;
    if (req) return;
    let cancelled = false;
    apiGet(`/project-requests/${id}`)
      .then((o) => !cancelled && setReq(o))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [id, req]);

  return (
    <ConfirmationShell kind="request" id={id} loading={!req && !error} error={error} title="Forespørselen er sendt" lead="Takk! Vi går gjennom prosjektet og kommer tilbake med prosjektpris og forslag til leveringsplan.">
      {req && (
        <dl className="space-y-3 px-6 py-5">
          <Row label="Prosjekttype">{req.projectType}</Row>
          <Row label="Sted">{req.location}</Row>
          {req.productNames?.length > 0 && <Row label="Produkter">{req.productNames.join(', ')}</Row>}
          {req.estimatedQuantityTonnes ? <Row label="Estimert mengde">{formatNumber(req.estimatedQuantityTonnes)} tonn</Row> : null}
          {req.startDate && <Row label="Ønsket oppstart">{formatDateLong(req.startDate)}</Row>}
          <Row label="Levering">{METHOD[req.deliveryMethod] ?? 'Ikke avklart'}</Row>
          <Row label="Kontakt">
            {req.customer.name}
            {req.customer.company ? `, ${req.customer.company}` : ''}
          </Row>
        </dl>
      )}
    </ConfirmationShell>
  );
}
